// Permits import — header normalization, fuzzy mapping, row parsing,
// and bulk insert. Mirrors the Activities import pattern.

import { supabase } from './supabase';
import type { PermitType, PermitStatus } from './dsrEntry';

async function getAuthUserId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user?.id) throw new Error('Not authenticated');
  return session.user.id;
}

async function getTenantId(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error('Not authenticated');
  const payload = JSON.parse(
    atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
  );
  const tenantId = payload.tenant_id as string | undefined;
  if (!tenantId) throw new Error('Tenant not resolved');
  return tenantId;
}

export type PermitField =
  | 'permit_type'
  | 'permit_number'
  | 'status'
  | 'issued_by'
  | 'issue_datetime'
  | 'expiry_datetime'
  | 'work_location'
  | 'ssr_verified'
  | 'comments';

export const REQUIRED_FIELDS: PermitField[] = [
  'permit_type',
  'permit_number',
  'issued_by',
  'issue_datetime',
];

export const ALL_FIELDS: { key: PermitField; label: string; required: boolean }[] = [
  { key: 'permit_type',     label: 'Permit Type',      required: true },
  { key: 'permit_number',   label: 'Permit Number',    required: true },
  { key: 'status',          label: 'Status',           required: false },
  { key: 'issued_by',       label: 'Issued By',        required: true },
  { key: 'issue_datetime',  label: 'Issue Date/Time',  required: true },
  { key: 'expiry_datetime', label: 'Expiry Date/Time', required: false },
  { key: 'work_location',   label: 'Work Location',    required: false },
  { key: 'ssr_verified',    label: 'SSR Verified',     required: false },
  { key: 'comments',        label: 'Comments',         required: false },
];

// Label → enum value. Accepts template labels ("Hot Work"), enum values
// ("hot_work"), and common variants.
const PERMIT_TYPE_MAP: Record<string, PermitType> = {
  hot_work: 'hot_work',
  hotwork: 'hot_work',
  confined_space_entry: 'confined_space_entry',
  confined_space: 'confined_space_entry',
  cse: 'confined_space_entry',
  critical_lift: 'critical_lift',
  electrical_isolation: 'electrical_isolation',
  electrical: 'electrical_isolation',
  loto: 'loto',
  lockout_tagout: 'loto',
  excavation: 'excavation',
  overwater_work: 'overwater_work',
  overwater: 'overwater_work',
  radiation: 'radiation',
  general_work: 'general_work',
  general: 'general_work',
  other: 'other',
};

const VALID_STATUSES: readonly PermitStatus[] = [
  'open', 'active', 'suspended', 'closed', 'cancelled',
] as const;

export function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s\-/]+/g, '_');
}

export function hasRequiredMozaicColumns(cols: string[]): boolean {
  const set = new Set(cols.map(normalizeHeader));
  return REQUIRED_FIELDS.every((r) => set.has(r));
}

export function detectColumnMapping(cols: string[]): Record<string, PermitField> {
  const out: Record<string, PermitField> = {};
  const taken = new Set<PermitField>();

  const patterns: [PermitField, RegExp[]][] = [
    ['permit_type',     [/^permit_type$/, /^type$/, /work.?type/]],
    ['permit_number',   [/^permit_number$/, /^number$/, /permit_?#/, /^reference$/, /permit_?no\b/]],
    ['status',          [/^status$/, /^state$/]],
    ['issued_by',       [/^issued_by$/, /^issuer$/, /^authorized_by$/]],
    ['issue_datetime',  [/^issue_date(time)?$/, /^start_date(time)?$/, /^start$/, /^issued_at$/, /issue_date/]],
    ['expiry_datetime', [/^expiry(_?date(time)?)?$/, /^expires?(_at)?$/, /^end_date(time)?$/, /expiry_date/]],
    ['work_location',   [/^work_location$/, /^location$/, /^area$/, /^site$/]],
    ['ssr_verified',    [/^ssr_verified$/, /^verified$/, /^is_verified$/]],
    ['comments',        [/^comments?$/, /^notes?$/, /^remarks?$/]],
  ];

  for (const col of cols) {
    const n = normalizeHeader(col);
    for (const [field, regs] of patterns) {
      if (taken.has(field)) continue;
      if (regs.some((r) => r.test(n))) {
        out[col] = field;
        taken.add(field);
        break;
      }
    }
  }
  return out;
}

export function identityMappingFromMozaicHeaders(
  cols: string[],
): Record<string, PermitField> {
  const out: Record<string, PermitField> = {};
  const known = new Set<string>(ALL_FIELDS.map((f) => f.key));
  for (const col of cols) {
    const n = normalizeHeader(col);
    if (known.has(n)) out[col] = n as PermitField;
  }
  return out;
}

function parseBool(raw: unknown): boolean {
  const s = String(raw ?? '').trim().toLowerCase();
  return ['true', '1', 'yes', 'y', 't'].includes(s);
}

// Accepts "YYYY-MM-DD HH:MM", "YYYY-MM-DDTHH:MM", Excel-parsed Dates, and
// ISO timestamps.
function parseDateTime(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (raw instanceof Date && !isNaN(raw.getTime())) return raw.toISOString();
  const s = String(raw).trim();
  if (!s) return null;
  // "YYYY-MM-DD HH:MM" → "YYYY-MM-DDTHH:MM:00"
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (m) {
    const hh = m[2].padStart(2, '0');
    return new Date(`${m[1]}T${hh}:${m[3]}:00`).toISOString();
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString();
}

function parsePermitType(raw: unknown): PermitType | null {
  const s = normalizeHeader(String(raw ?? ''));
  if (!s) return null;
  return PERMIT_TYPE_MAP[s] ?? null;
}

function parseStatus(raw: unknown): PermitStatus {
  const s = normalizeHeader(String(raw ?? 'open'));
  return (VALID_STATUSES as readonly string[]).includes(s)
    ? (s as PermitStatus)
    : 'open';
}

export interface ParsedPermit {
  permit_type: PermitType;
  permit_number: string;
  status: PermitStatus;
  issued_by: string;
  issued_at: string;
  expires_at: string | null;
  work_location: string | null;
  is_verified: boolean;
  comments: string | null;
}

export function applyMappingToRows(
  rawRows: Record<string, unknown>[],
  columnMap: Record<string, PermitField>,
): { rows: ParsedPermit[]; errors: string[] } {
  const errors: string[] = [];
  const rows: ParsedPermit[] = [];

  const field2col: Partial<Record<PermitField, string>> = {};
  for (const [col, field] of Object.entries(columnMap)) field2col[field] = col;

  rawRows.forEach((r, i) => {
    const rowNum = i + 2;
    const pick = (f: PermitField): unknown => {
      const col = field2col[f];
      return col ? r[col] : undefined;
    };
    const str = (f: PermitField) => String(pick(f) ?? '').trim();

    const permitNumber = str('permit_number');
    const issuedBy = str('issued_by');
    const permitType = parsePermitType(pick('permit_type'));
    const issuedAt = parseDateTime(pick('issue_datetime'));

    if (!permitType) {
      errors.push(`Row ${rowNum}: unrecognized permit type "${str('permit_type')}"`);
      return;
    }
    if (!permitNumber) { errors.push(`Row ${rowNum}: missing permit number`); return; }
    if (!issuedBy) { errors.push(`Row ${rowNum}: missing issued_by`); return; }
    if (!issuedAt) { errors.push(`Row ${rowNum}: missing or invalid issue_datetime`); return; }

    rows.push({
      permit_type: permitType,
      permit_number: permitNumber,
      status: parseStatus(pick('status')),
      issued_by: issuedBy,
      issued_at: issuedAt,
      expires_at: parseDateTime(pick('expiry_datetime')),
      work_location: str('work_location') || null,
      is_verified: parseBool(pick('ssr_verified')),
      comments: str('comments') || null,
    });
  });

  return { rows, errors };
}

// ─── Saved column mappings (module = 'permits') ─────────────────────────────
// Reuses pob_column_mappings with module discriminator — see migration
// 20260415000004_pob_column_mappings_module.sql.

export interface PermitColumnMapping {
  id: string;
  tenant_id: string;
  mapping_name: string;
  column_map: Record<string, PermitField>;
  is_default: boolean;
  module: 'permits';
  created_at: string;
  updated_at: string;
}

export async function getDefaultPermitMapping(): Promise<PermitColumnMapping | null> {
  const { data, error } = await supabase
    .from('pob_column_mappings')
    .select('*')
    .eq('module', 'permits')
    .eq('is_default', true)
    .maybeSingle();
  if (error) return null;
  return (data ?? null) as PermitColumnMapping | null;
}

export async function savePermitMapping(params: {
  name: string;
  columnMap: Record<string, PermitField>;
  makeDefault?: boolean;
}): Promise<PermitColumnMapping> {
  const tenantId = await getTenantId();
  const createdBy = await getAuthUserId();

  if (params.makeDefault) {
    await supabase
      .from('pob_column_mappings')
      .update({ is_default: false, updated_at: new Date().toISOString() })
      .eq('tenant_id', tenantId)
      .eq('module', 'permits')
      .eq('is_default', true);
  }

  const { data, error } = await supabase
    .from('pob_column_mappings')
    .insert({
      tenant_id: tenantId,
      mapping_name: params.name,
      column_map: params.columnMap,
      is_default: !!params.makeDefault,
      module: 'permits',
      created_by: createdBy,
    })
    .select('*')
    .single();
  if (error) throw new Error(error.message);
  return data as PermitColumnMapping;
}

export async function bulkInsertPermits(
  dsrId: string,
  permits: ParsedPermit[],
  tenantId: string,
): Promise<{ inserted: number; errors: string[] }> {
  if (permits.length === 0) return { inserted: 0, errors: [] };
  const userId = await getAuthUserId();
  const verifiedAt = new Date().toISOString();
  const payload = permits.map((p) => ({
    dsr_report_id: dsrId,
    tenant_id: tenantId,
    created_by: userId,
    permit_type: p.permit_type,
    permit_number: p.permit_number,
    status: p.status,
    issued_by: p.issued_by,
    issued_at: p.issued_at,
    expires_at: p.expires_at,
    work_location: p.work_location,
    is_verified: p.is_verified,
    verified_by: p.is_verified ? userId : null,
    verified_at: p.is_verified ? verifiedAt : null,
    comments: p.comments,
  }));
  const { error, count } = await supabase
    .from('dsr_permit_entries')
    .insert(payload, { count: 'exact' });
  if (error) return { inserted: 0, errors: [error.message] };
  return { inserted: count ?? permits.length, errors: [] };
}
